import { PageShell } from "@superadmin/components/PageShell";
import {
	AssignNumberDialog,
	DefaultCallerIdDialog,
	SharedLineBatchesDialog,
} from "@superadmin/components/PhoneNumberDialogs";
import { orpc } from "@superadmin/utils/orpc.tanstack.client";
import { useQuery } from "@tanstack/react-query";
import { LoadingSpinner } from "@voicely/ui-mui/components/LoadingSpinner";
import { Chip } from "@voicely/ui-mui/data-display/Chip";
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
} from "@voicely/ui-mui/data-display/Table";
import { Typography } from "@voicely/ui-mui/data-display/Typography";
import { Alert } from "@voicely/ui-mui/feedback/Alert";
import { Button } from "@voicely/ui-mui/form/Button";
import { Paper } from "@voicely/ui-mui/layout/Paper";
import { Stack } from "@voicely/ui-mui/layout/Stack";
import { useUrlState } from "@voicely/web-shared/use_url_state";
import { z } from "zod";

/** Which dialog is open. In the URL so a refresh or a pasted link lands on it. */
const panelZod = z.enum(["assign", "default-caller", "shared-batches"]).nullable();

/**
 * Every DID on the platform. The one view where another workspace having
 * claimed the shared caller ID is visible — the tenant dashboard shows each
 * workspace only its own numbers.
 *
 * Also where a super-admin gives a workspace a number and changes the shared
 * outbound caller ID. A workspace with no number of its own can place single
 * test calls from the shared ID and cannot run batches.
 */
export const PhoneNumbersPage = () => {
	const [panel, setPanel] = useUrlState<"assign" | "default-caller" | "shared-batches" | null>(
		"panel",
		panelZod,
		null,
	);
	const numbersQuery = useQuery(orpc.superAdmin.listPhoneNumbers.queryOptions());
	const callerQuery = useQuery(orpc.superAdmin.defaultCallerId.queryOptions());
	const grantsQuery = useQuery(orpc.superAdmin.sharedLineBatchGrants.queryOptions());

	if (numbersQuery.isPending) {
		return (
			<PageShell title="Phone numbers">
				<LoadingSpinner />
			</PageShell>
		);
	}

	if (numbersQuery.error) {
		return (
			<PageShell title="Phone numbers">
				<Alert severity="error">{numbersQuery.error.message}</Alert>
			</PageShell>
		);
	}

	const numbers = numbersQuery.data;

	return (
		<PageShell
			title="Phone numbers"
			subtitle="Every mapping, every workspace"
			actions={
				<Stack direction="row" spacing={1}>
					<Button variant="outlined" onClick={() => setPanel("shared-batches")}>
						Batches on shared ID
					</Button>
					<Button variant="outlined" onClick={() => setPanel("default-caller")}>
						Shared caller ID
					</Button>
					<Button variant="contained" onClick={() => setPanel("assign")}>
						Assign number
					</Button>
				</Stack>
			}
		>
			<Typography variant="body2" sx={{ mb: 2 }}>
				Shared caller ID:{" "}
				<strong>
					{callerQuery.data?.effective ? `+${callerQuery.data.effective}` : "none configured"}
				</strong>
				{callerQuery.data?.override && (
					<Chip label="overridden" size="small" color="info" sx={{ ml: 1 }} />
				)}
			</Typography>

			<Typography variant="body2" sx={{ mb: 2 }}>
				Allowed to run batches on it:{" "}
				{(grantsQuery.data ?? []).length === 0 ? (
					<strong>no workspace</strong>
				) : (
					(grantsQuery.data ?? []).map((grant) => (
						<Chip
							key={grant.teamId}
							label={grant.teamName ?? grant.teamId}
							size="small"
							sx={{ mr: 0.5 }}
						/>
					))
				)}
			</Typography>

			{numbers.length === 0 ? (
				<Typography color="text.secondary">No phone numbers mapped.</Typography>
			) : (
				<TableContainer component={Paper} variant="outlined">
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Number</TableCell>
								<TableCell>Workspace</TableCell>
								<TableCell>Agent</TableCell>
								<TableCell>Flags</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{numbers.map((row) => {
								// The invariant this page exists to check: the answering
								// agent must belong to the workspace that owns the number.
								const crossTenant = row.agentTeamId !== null && row.agentTeamId !== row.teamId;

								return (
									<TableRow key={row.e164}>
										<TableCell sx={{ whiteSpace: "nowrap" }}>{row.e164}</TableCell>
										<TableCell>{row.teamName ?? row.teamId}</TableCell>
										<TableCell>{row.agentName ?? row.agentId}</TableCell>
										<TableCell>
											<Stack direction="row" spacing={0.5}>
												{!row.enabled && <Chip label="disabled" size="small" />}
												{row.isSharedCallerId && (
													<Chip label="shared caller ID" size="small" color="info" />
												)}
												{crossTenant && (
													<Chip label="CROSS-TENANT AGENT" size="small" color="error" />
												)}
											</Stack>
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</TableContainer>
			)}

			{panel === "assign" && <AssignNumberDialog onClose={() => setPanel(null)} />}
			{panel === "default-caller" && <DefaultCallerIdDialog onClose={() => setPanel(null)} />}
			{panel === "shared-batches" && <SharedLineBatchesDialog onClose={() => setPanel(null)} />}
		</PageShell>
	);
};
